// =============================================================================
// MeuDriver — Hook: useStudentDashboardData (Batch K4)
// Dados específicos para os novos widgets do dashboard do estudante.
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface NextLessonData {
  id: string
  scheduled_at: string
  status: string
  pickup_address: string | null
  instructor_name: string | null
  instructor_avatar: string | null
}

export interface MonthSummaryData {
  completedCount:  number
  completionRate:  number   // percentual 0-100
  averageRating:   number | null
}

// ---------------------------------------------------------------------------
// useNextLesson — próximo agendamento futuro (pending | confirmed)
// ---------------------------------------------------------------------------

export function useNextLesson() {
  const { user } = useAuth()

  return useQuery<NextLessonData | null>({
    queryKey: ['next-lesson', user?.id],
    queryFn: async () => {
      const today = new Date().toISOString()

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          scheduled_at,
          status,
          pickup_address,
          instructor:instructors!bookings_instructor_id_fkey(
            profile:profiles!instructors_profile_id_fkey(
              full_name,
              avatar_url
            )
          )
        `)
        .eq('student_id', user!.id)
        .in('status', ['pending_payment', 'confirmed'])
        .gte('scheduled_at', today)
        .order('scheduled_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      const row = data as Record<string, unknown>
      const instructor = row.instructor as Record<string, unknown> | null
      const profile    = instructor?.profile as Record<string, unknown> | null

      return {
        id:               String(row.id),
        scheduled_at:     String(row.scheduled_at),
        status:           String(row.status),
        pickup_address:   (row.pickup_address as string | null) ?? null,
        instructor_name:  (profile?.full_name as string | null) ?? null,
        instructor_avatar:(profile?.avatar_url as string | null) ?? null,
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
  })
}

// ---------------------------------------------------------------------------
// useStudentMonthSummary — resumo do mês corrente
// ---------------------------------------------------------------------------

export function useStudentMonthSummary() {
  const { user } = useAuth()

  return useQuery<MonthSummaryData>({
    queryKey: ['month-summary', user?.id],
    queryFn: async () => {
      const now        = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()

      // Total de bookings no mês (exceto cancelados)
      const { data: allRows, error: allErr } = await supabase
        .from('bookings')
        .select('id, status')
        .eq('student_id', user!.id)
        .gte('scheduled_at', monthStart)
        .lte('scheduled_at', monthEnd)
        .not('status', 'in', '("cancelled_by_student","cancelled_by_instructor","no_show")')

      if (allErr) throw allErr

      const total     = (allRows ?? []).length
      const completed = (allRows ?? []).filter((b) => b.status === 'completed').length

      // Avaliações dadas no mês
      const { data: ratingsRows, error: ratingsErr } = await supabase
        .from('reviews')
        .select('rating')
        .eq('student_id', user!.id)
        .gte('created_at', monthStart)
        .lte('created_at', monthEnd)

      if (ratingsErr) throw ratingsErr

      const ratings = (ratingsRows ?? []) as { rating: number }[]
      const avgRating =
        ratings.length > 0
          ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length
          : null

      return {
        completedCount: completed,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        averageRating:  avgRating !== null ? Math.round(avgRating * 10) / 10 : null,
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  })
}
